import React, { memo, useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Connector, ConnectorInfo, Connectors } from 'types/types';
import DagsSectionConfig from 'dag-section';
import { ConnectorDetails } from './ConnectorDetails';
import { ConnectorCardComponent } from './ConnectorCardComponent';
import ConnectorEmptyPageMessage from './ErrorPage/ConnectorEmptyPageMessage';
import { useConnectorSelectionContext } from './contexts/ConnectorSelectionContextProvide';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: theme.spacing(1)
  },
  emptyContainer: {
    marginTop: theme.spacing(-4),
    paddingBottom: theme.spacing(50)
  }
}));

type AllConnectorsListPageProps = {
  searchValue: string;
  data: Connectors;
};

export const AllConnectorsListPage: React.FC<AllConnectorsListPageProps> = memo(
  ({ searchValue, data }: AllConnectorsListPageProps) => {
    const classes = useStyles();
    const [openDialog, setOpenDialog] = useState(false);
    const [info, setInfo] = useState<ConnectorInfo>();
    const {
      connectorsSelectionStatus: connectorsSelectionStatus
    } = useConnectorSelectionContext();

    const connectorsList = useMemo(() => {
      if (searchValue.length === 0) {
        return data.connectors.items;
      } else {
        const filteredItems = data.connectors.items.filter((item: Connector) =>
          item.name.toLowerCase().includes(searchValue.toLowerCase())
        );

        return filteredItems || [];
      }
    }, [data.connectors.items, searchValue]);

    const handleOpenDialog = useCallback(
      (item) => {
        setInfo({
          ...item,
          companyKey: data.companyKey,
          isPsaProduct: data.isPsaProduct
        });
        setOpenDialog(true);
      },
      [data.companyKey, data.isPsaProduct]
    );

    const handleCloseDialog = useCallback(() => {
      setInfo(undefined);
      setOpenDialog(false);
    }, []);

    const {
      installedConnectors,
      uninstalledConnectors,
      allConnectors
    } = useMemo(() => {
      const installedConnectors: Connector[] = [];
      const uninstalledConnectors: Connector[] = [];
      const dagsSection = new DagsSectionConfig();
      (connectorsList || []).forEach((item: Connector) => {
        item.dagsSection = dagsSection.getDagsSection(
          item.shortName,
          data.isPsaProduct,
          item?.installMethod
        );
        if (item.installed) {
          installedConnectors.push(item);
        } else {
          uninstalledConnectors.push(item);
        }
      });
      return {
        installedConnectors: installedConnectors,
        uninstalledConnectors: uninstalledConnectors,
        allConnectors: connectorsList || []
      };
    }, [connectorsList, data.isPsaProduct]);

    const connectorsToDisplay = useMemo(() => {
      if (connectorsSelectionStatus === 'connectors.all') {
        return allConnectors;
      } else if (connectorsSelectionStatus === 'connectors.installed') {
        return installedConnectors;
      } else return uninstalledConnectors;
    }, [
      allConnectors,
      connectorsSelectionStatus,
      installedConnectors,
      uninstalledConnectors
    ]);
    const connectorDetails = useMemo(() => {
      return (
        info && (
          <ConnectorDetails
            open={openDialog}
            handleClose={handleCloseDialog}
            info={info as ConnectorInfo}
          />
        )
      );
    }, [handleCloseDialog, info, openDialog]);

    return (
      <div className={classes.container} data-testid="Connector-All-Page-Item">
        {connectorsToDisplay.length ? (
          connectorsToDisplay.map((item: Connector, index: number) => (
            <ConnectorCardComponent
              info={item}
              index={index}
              handleOpenDialog={handleOpenDialog}
              key={item.name}
            />
          ))
        ) : (
          <div
            className={classes.emptyContainer}
            data-testid="check-empty-page"
          >
            <ConnectorEmptyPageMessage />
          </div>
        )}
        {connectorDetails}
      </div>
    );
  }
);
AllConnectorsListPage.displayName = 'AllConnectorsListPage';
